"use client";
import React, { useEffect, useMemo, useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Container,
  Box,
  Badge,
  Link,
  useTheme,
  useMediaQuery,
  Popover,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Snackbar,
  Alert,
  AlertColor,
  Tooltip,
} from "@mui/material";
import {
  ListAlt,
  Key,
  Reviews,
  TableChart,
  Create,
  FactCheck,
  ViewList,
  Logout,
  CalendarMonth,
  HowToReg,
  AccountCircle,
  EditNote,
  Dns,
  AutoStories,
  UploadFile,
  Dataset,
  PlaylistRemove,
  FormatClear,
  MenuOpen,
  Scale,
  ErrorOutline,
  WarningAmberOutlined
} from "@mui/icons-material";
import MenuIcon from "@mui/icons-material/Menu";
import NotificationsIcon from "@mui/icons-material/Notifications";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import GroupIcon from "@mui/icons-material/Group";
import ClearIcon from "@mui/icons-material/NotInterested";
import { useConfig } from "./ConfigProvider";
import Image from "next/image";
import { useRouter, usePathname } from "next/navigation";
import ModalBox from "./formrenderer/ModalBox";
import designNewFormParams from "../layouts/DesignNewFormParams.json";
import newApplication from "../layouts/NewApplication.json";
import uploadEDD from "../layouts/UploadEDD.json";
import deleteForm from "../layouts/DeleteForm.json";
import deleteFormType from "../layouts/DeleteFormType.json";
import { useAuth } from "./AuthProvider";
import { useFormData } from "./FormDataProvider";
import { get, set } from "./formrenderer/utils/Utils";
import { useIdleSessionTimer } from "@/hooks/useIdleSessionTimer";
import SessionAlertModal from "@/components/SessionAlertModal";

const alertColors: AlertColor[] = ["success", "info", "warning", "error"];

type MenuItemType = {
  PKey: number;
  Name: string;
  MenuGroup: string;
  Alias: string;
  Order: number;
  ModifyDate: string;
  ModifyUser: string;
};

const Header: React.FC = () => {
  const [adminAnchorEl, setAdminAnchorEl] = React.useState<null | HTMLElement>(
    null,
  );
  const adminMenuOpen = Boolean(adminAnchorEl);
  const [userAnchorEl, setUserAnchorEl] = React.useState<null | HTMLElement>(
    null,
  );
  const userMenuOpen = Boolean(userAnchorEl);
  const [mobileMenuAnchorEl, setMobileMenuAnchorEl] =
    React.useState<null | HTMLElement>(null);
  const mobileMenuOpen = Boolean(mobileMenuAnchorEl);
  const [newLayoutOpen, setNewLayoutOpen] = React.useState<Boolean>(false);
  const [newFormOpen, setNewFormOpen] = React.useState<Boolean>(false);
  const [uploadEDDOpen, setUploadEDDOpen] = React.useState<Boolean>(false);
  const [deleteFormOpen, setDeleteFormOpen] = React.useState<Boolean>(false);
  const [deleteFormTypeOpen, setDeleteFormTypeOpen] =
    React.useState<Boolean>(false);

  const { appName, apiUrl, loginUrl } = useConfig();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const pathname = usePathname();
  const { account, logout, isAdmin, isInRole, registrationError } = useAuth();
  const { formData, navigatePage, setFormDataElement, getPage } = useFormData();
  const [validationMessage, setValidationMessage] = useState<string | null>(
    null,
  );
  const [validationSeverity, setValidationSeverity] =
    useState<AlertColor>("error");
  const [popoverAnchorEl, setPopoverAnchorEl] = useState<null | HTMLElement>(
    null,
  );
  const isPopoverOpen = Boolean(popoverAnchorEl);
  const [isAlertOpen, setIsAlertOpen] = useState<boolean>(false);

  const validationErrors = get(formData, "validationResult_errors");
  const validationWarnings = get(formData, "validationResult_warnings");
  const errors = Object.values(validationErrors ?? {});
  const warnings = Object.values(validationWarnings ?? {});
  const validationMessages = [
    ...errors.map((message, index) => ({ key: `error-${index}`, message, severity: "error" as const })),
    ...warnings.map((message, index) => ({ key: `warning-${index}`, message, severity: "warning" as const })),
  ];
  const notificationCount = validationMessages.length;

  const isUserAdmin = isAdmin();
  const isDeveloper = isInRole("eForm_Developer");
  const isLoggedIn = account != null;

  const isRegistrationError = !!registrationError;

  // Extract MenuItems from formData
  const menuItems: MenuItemType[] = get(formData, "MenuItems", []);

  // Use useMemo to filter and sort menu items
  const adminFormItems = useMemo(() => {
    return menuItems
      .filter(item => item.MenuGroup === "AdminForms")
      .sort((a, b) => a.Order - b.Order);
  }, [menuItems]);

  const usersItems = useMemo(() => {
    return menuItems
      .filter(item => item.MenuGroup === "Users")
      .sort((a, b) => a.Order - b.Order);
  }, [menuItems]);

  const mainItems = useMemo(() => {
    return menuItems
      .filter(item => item.MenuGroup === "Main")
      .sort((a, b) => a.Order - b.Order);
  }, [menuItems]);

  const developmentItems = useMemo(() => {
    return menuItems
      .filter(item => item.MenuGroup === "Development")
      .sort((a, b) => a.Order - b.Order);
  }, [menuItems]);

  const handleAdminMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAdminAnchorEl(event.currentTarget);
  };

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAdminAnchorEl(null);
    setUserAnchorEl(null);
    setMobileMenuAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleMenuClose();
    setIsAlertOpen(false);
  };

  const handleStay = () => {
    resetTimer();
    setIsAlertOpen(false);
  };

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMenuAnchorEl(event.currentTarget);
  };

  const handleNavigation = (path: string) => {
    navigatePage(path);
    handleMenuClose();
  };

  const handleCloseSnackbar = () => {
    setValidationMessage(null);
  };

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (notificationCount > 0) {
      setPopoverAnchorEl(event.currentTarget);
    }
  };

  const handlePopoverClose = () => {
    setPopoverAnchorEl(null);
  };

  const handleNewFormOpen = () => {
    // set initoal form data for new application form
    set(formData, "NewApplication", {});
    setNewFormOpen(true);
  };

  const handleNewFormClose = () => {
    setNewFormOpen(false);
  };

  const handleUploadEDDOpen = () => {
    // set initoal form data for new application form
    set(formData, "UploadEDD", {});
    setUploadEDDOpen(true);
  };

  const handleUploadEDDClose = () => {
    setUploadEDDOpen(false);
  };

  // Function to clear error messages
  const handleClearErrors = () => {
    // Logic to clear the errors from the state or context
    setFormDataElement("validationResult_errors", null);
    setFormDataElement("validationResult_warnings", null);
    setFormDataElement("validationResult_resultMessage", null);
    handlePopoverClose(); // Close the popover after clearing
  };

  const { alertRemaining, resetTimer } = useIdleSessionTimer(
    handleLogout,
    isLoggedIn,
  );

  // Effect to close modals on route change
  useEffect(() => {
    setNewLayoutOpen(false);
    setNewFormOpen(false);
    setDeleteFormOpen(false);
  }, [pathname]); // Only run on pathname change

  useEffect(() => {
    const resultMessage = get(formData, "validationResult_resultMessage", null);
    if (resultMessage) {
      setValidationMessage(
        notificationCount > 0 ? `${resultMessage}` : resultMessage,
      );
      const severity = get(formData, "validationResult_severity", 3);
      if (severity >= 0 && severity <= 3) {
        setValidationSeverity(alertColors[severity]);
      }
    }
  }, [formData, notificationCount]);

  useEffect(() => {
    if (alertRemaining !== null && !isAlertOpen) {
      setIsAlertOpen(true);
    }
  }, [alertRemaining, isAlertOpen]);

  return (
    <AppBar position="static">
      <Container maxWidth="xl">
        <Toolbar disableGutters>
          <IconButton
            size="large"
            edge="start"
            color="inherit"
            aria-label="menu"
            onClick={handleMobileMenuOpen}
            sx={{ display: isMobile ? "flex" : "none" }} // Conditionally display on narrow screens
          >
            <MenuIcon />
          </IconButton>

          <Link href="http://rbdmsonline.org">
            <Image
              src="/rbdms-logo.svg"
              alt="RBDMS Logo"
              width={180}
              height={160}
              priority
              className="eform-header-img"
            />
          </Link>

          <Typography
            variant="h6"
            component="div"
            sx={{ fontSize: "1.25rem", fontWeight: "bold", flexGrow: 1 }}
          >
            {appName}
          </Typography>

          {account && !isMobile && !isRegistrationError && (
            <>
              <Button
                variant="contained"
                disableElevation
                onClick={() => handleNavigation("/reports")}
              >
                Reports
              </Button>
              <Button
                variant="contained"
                disableElevation
                onClick={() => handleNewFormOpen()}
              >
                New Application
              </Button>
              <Button
                variant="contained"
                disableElevation
                onClick={() => handleUploadEDDOpen()}
              >
                Bulk EDD Upload
              </Button>
              <Button
                variant="contained"
                disableElevation
                onClick={() => handleNavigation("/datadict")}
              >
                Data Dictionaries
              </Button>
              {/* <Button color="inherit" onClick={() => handleNavigation('/reviewtasks')}>Review Tasks</Button> */}
              {mainItems.map((item: MenuItemType) => (
                <Button
                  key={item.PKey}
                  variant="contained"
                  disableElevation
                  onClick={() => handleNavigation(item.Alias)}
                >
                  {item.Name}
                </Button>
              ))}
              {isUserAdmin && (
                <>
                  <Button
                    id="demo-customized-button"
                    aria-controls={adminMenuOpen ? "admin-menu" : undefined}
                    aria-haspopup="true"
                    aria-expanded={adminMenuOpen ? "true" : undefined}
                    variant="contained"
                    disableElevation
                    onClick={handleAdminMenuOpen}
                    endIcon={<KeyboardArrowDownIcon />}
                  >
                    Admin
                  </Button>
                  <Menu
                    id="admin-menu"
                    disableScrollLock
                    className="eform-admin-menu"
                    anchorEl={adminAnchorEl}
                    open={adminMenuOpen}
                    onClose={handleMenuClose}
                    onClick={handleMenuClose}
                    transformOrigin={{ horizontal: "right", vertical: "top" }}
                    anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                  >
                    {isDeveloper && (
                      <>
                        <Typography
                          variant="subtitle1"
                          color="primary"
                          className="eform-menu-header"
                        >
                          Administrative Forms
                        </Typography>
                        <MenuItem
                          onClick={() =>
                            handleNavigation("/admin/user-permissions")
                          }
                        >
                          <GroupIcon className="eform-menu-icon" />
                          <Typography variant="inherit">
                            User Permissions
                          </Typography>
                        </MenuItem>
                        <MenuItem onClick={() => setNewLayoutOpen(true)}>
                          <EditNote className="eform-menu-icon" />
                          <Typography variant="inherit">
                            Design New Form
                          </Typography>
                        </MenuItem>
                        <MenuItem onClick={() => setDeleteFormOpen(true)}>
                          <PlaylistRemove className="eform-menu-icon" />
                          <Typography variant="inherit">Delete Form</Typography>
                        </MenuItem>
                        <MenuItem onClick={() => setDeleteFormTypeOpen(true)}>
                          <FormatClear className="eform-menu-icon" />
                          <Typography variant="inherit">
                            Delete Form Type
                          </Typography>
                        </MenuItem>
                        <MenuItem
                          onClick={() =>
                            handleNavigation("/admin/edit-lookups")
                          }
                        >
                          <Dns className="eform-menu-icon" />
                          <Typography variant="inherit">
                            Edit Lookups
                          </Typography>
                        </MenuItem>
                        <MenuItem
                          onClick={() =>
                            handleNavigation("/admin/edit-datatables")
                          }
                        >
                          <Dataset className="eform-menu-icon" />
                          <Typography variant="inherit">
                            Edit Form Data Tables
                          </Typography>
                        </MenuItem>
                        <MenuItem
                          onClick={() => handleNavigation("/admin/edit-pages")}
                        >
                          <AutoStories className="eform-menu-icon" />
                          <Typography variant="inherit">Edit Pages</Typography>
                        </MenuItem>
                        <MenuItem
                          onClick={() =>
                            handleNavigation("/admin/edit-menuitems")
                          }
                        >
                          <MenuOpen className="eform-menu-icon" />
                          <Typography variant="inherit">
                            Edit Menu Items
                          </Typography>
                        </MenuItem>
                        <MenuItem
                          onClick={() =>
                            handleNavigation("/admin/upload-documents")
                          }
                        >
                          <UploadFile className="eform-menu-icon" />
                          <Typography variant="inherit">
                            Upload Documents
                          </Typography>
                        </MenuItem>
                        {adminFormItems.map((item: MenuItemType) => (
                          <MenuItem
                            key={item.PKey}
                            onClick={() => handleNavigation(item.Alias)}
                          >
                            <Typography variant="inherit">
                              {item.Name}
                            </Typography>
                          </MenuItem>
                        ))}
                      </>
                    )}
                    <Typography
                      variant="subtitle1"
                      color="primary"
                      className="eform-menu-header"
                    >
                      Sample Result Lookups
                    </Typography>
                    <MenuItem
                      onClick={() => handleNavigation("/admin/list-parameters")}
                    >
                      <ViewList className="eform-menu-icon" />
                      <Typography variant="inherit">
                        Manage Parameters
                      </Typography>
                    </MenuItem>
                    <MenuItem
                      onClick={() =>
                        handleNavigation("/admin/list-result-methods")
                      }
                    >
                      <FactCheck className="eform-menu-icon" />
                      <Typography variant="inherit">
                        Manage Result Methods
                      </Typography>
                    </MenuItem>
                    <MenuItem
                      onClick={() => handleNavigation("/admin/list-units")}
                    >
                      <Scale className="eform-menu-icon" />
                      <Typography variant="inherit">Manage Units</Typography>
                    </MenuItem>
                    {isDeveloper && (
                      <>
                        <Typography
                          variant="subtitle1"
                          color="primary"
                          className="eform-menu-header"
                        >
                          Users
                        </Typography>
                        <MenuItem
                          onClick={() =>
                            window.open(`${loginUrl}/User`, "_blank")
                          }
                        >
                          <HowToReg className="eform-menu-icon" />
                          <Typography variant="inherit">
                            New Registrations
                          </Typography>
                        </MenuItem>
                        {usersItems.map((item: MenuItemType) => (
                          <MenuItem
                            key={item.PKey}
                            onClick={() => handleNavigation(item.Alias)}
                          >
                            <Typography variant="inherit">
                              {item.Name}
                            </Typography>
                          </MenuItem>
                        ))}
                      </>
                    )}
                    {isDeveloper && (
                      <>
                        <Typography
                          variant="subtitle1"
                          color="primary"
                          className="eform-menu-header"
                        >
                          Development
                        </Typography>
                        <MenuItem
                          onClick={() =>
                            handleNavigation("/admin/change-status-actions")
                          }
                        >
                          <Typography variant="inherit">
                            Change Status Actions
                          </Typography>
                        </MenuItem>
                        <MenuItem
                          onClick={() => handleNavigation("/admin/edd-mapping")}
                        >
                          <Typography variant="inherit">EDD Mapping</Typography>
                        </MenuItem>
                        <MenuItem
                          onClick={() =>
                            handleNavigation("/admin/edd-translator-download")
                          }
                        >
                          <Typography variant="inherit">
                            EDD Translator Download
                          </Typography>
                        </MenuItem>
                        <MenuItem
                          onClick={() =>
                            handleNavigation("/admin/edd-translator-upload")
                          }
                        >
                          <Typography variant="inherit">
                            EDD Translator Upload
                          </Typography>
                        </MenuItem>
                        {developmentItems.map((item: MenuItemType) => (
                          <MenuItem
                            key={item.PKey}
                            onClick={() => handleNavigation(item.Alias)}
                          >
                            <Typography variant="inherit">
                              {item.Name}
                            </Typography>
                          </MenuItem>
                        ))}
                        <MenuItem
                          onClick={() =>
                            window.open(`${apiUrl}/Admin/logFile`, "_blank")
                          }
                        >
                          <Typography variant="inherit">Server Log</Typography>
                        </MenuItem>
                      </>
                    )}
                  </Menu>
                </>
              )}
            </>
          )}

          {account && !isRegistrationError && (
            <>
              <IconButton
                size="large"
                aria-label="show validation errors"
                color="inherit"
                onClick={handlePopoverOpen}
                sx={{
                  "&:hover": {
                    backgroundColor: "#1e8a78",
                  },
                }}
              >
                <Badge badgeContent={notificationCount} color={errors.length > 0 ? "error" : "warning"}>
                  <NotificationsIcon />
                </Badge>
              </IconButton>

              <Typography variant="inherit" sx={{ ml: 2 }}>
                {account.name}
              </Typography>

              <IconButton
                size="large"
                edge="end"
                color="inherit"
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleUserMenuOpen}
                sx={{
                  "&:hover": {
                    backgroundColor: "#1e8a78",
                  },
                }}
              >
                <AccountCircle />
              </IconButton>

              <Menu
                id="user-menu"
                disableScrollLock
                anchorEl={userAnchorEl}
                open={userMenuOpen}
                onClose={handleMenuClose}
                onClick={handleMenuClose}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
              >
                {/* <MenuItem onClick={handleMenuClose}>
                <Key className="eform-menu-icon" />
                <Typography variant="inherit">Change Password</Typography>
              </MenuItem> */}
                <MenuItem onClick={handleLogout}>
                  <Logout className="eform-menu-icon" />
                  <Typography variant="inherit">Logout</Typography>
                </MenuItem>
              </Menu>

              <Menu
                id="mobile-menu"
                disableScrollLock
                anchorEl={mobileMenuAnchorEl}
                open={mobileMenuOpen}
                onClose={handleMenuClose}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
              >
                <MenuItem onClick={() => handleNavigation("/reports")}>
                  <ListAlt className="eform-menu-icon" />
                  <Typography variant="inherit">Reports</Typography>
                </MenuItem>
                <MenuItem onClick={() => handleNewFormOpen()}>
                  <Create className="eform-menu-icon" />
                  <Typography variant="inherit">New Application</Typography>
                </MenuItem>
                <MenuItem onClick={() => handleUploadEDDOpen()}>
                  <UploadFile className="eform-menu-icon" />
                  <Typography variant="inherit">Bulk EDD Upload</Typography>
                </MenuItem>
                <MenuItem onClick={() => handleNavigation("/datadict")}>
                  <TableChart className="eform-menu-icon" />
                  <Typography variant="inherit">Data Dictionaries</Typography>
                </MenuItem>
                <MenuItem onClick={() => handleNavigation("/reviewtasks")}>
                  <Reviews className="eform-menu-icon" />
                  <Typography variant="inherit">Review Tasks</Typography>
                </MenuItem>
                {mainItems.map((item: MenuItemType) => (
                  <MenuItem
                    onClick={() => handleNavigation(item.Alias)}
                    key={item.PKey}
                  >
                    <Typography variant="inherit">{item.Name}</Typography>
                  </MenuItem>
                ))}
                {isUserAdmin && (
                  <MenuItem onClick={handleAdminMenuOpen}>
                    <Typography variant="inherit">Admin</Typography>
                    <KeyboardArrowDownIcon />
                  </MenuItem>
                )}
                {isUserAdmin && (
                  <Menu
                    id="admin-menu-mobile"
                    disableScrollLock
                    anchorEl={adminAnchorEl}
                    open={adminMenuOpen}
                    onClose={handleMenuClose}
                    transformOrigin={{ horizontal: "right", vertical: "top" }}
                    anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                  >
                    <Typography
                      variant="subtitle1"
                      color="primary"
                      className="eform-menu-header"
                    >
                      Administrative Forms
                    </Typography>
                    <MenuItem
                      onClick={() =>
                        handleNavigation("/admin/user-permissions")
                      }
                    >
                      <GroupIcon className="eform-menu-icon" />
                      <Typography variant="inherit">
                        User Permissions
                      </Typography>
                    </MenuItem>
                    <MenuItem onClick={() => setNewLayoutOpen(true)}>
                      <EditNote className="eform-menu-icon" />
                      <Typography variant="inherit">Design New Form</Typography>
                    </MenuItem>
                    <MenuItem onClick={() => setDeleteFormOpen(true)}>
                      <PlaylistRemove className="eform-menu-icon" />
                      <Typography variant="inherit">Delete Form</Typography>
                    </MenuItem>
                    <MenuItem onClick={() => setDeleteFormTypeOpen(true)}>
                      <FormatClear className="eform-menu-icon" />
                      <Typography variant="inherit">
                        Delete Form Type
                      </Typography>
                    </MenuItem>
                    <MenuItem
                      onClick={() => handleNavigation("/admin/edit-lookups")}
                    >
                      <Dns className="eform-menu-icon" />
                      <Typography variant="inherit">Edit Lookups</Typography>
                    </MenuItem>
                    <MenuItem
                      onClick={() => handleNavigation("/admin/edit-datatables")}
                    >
                      <Dataset className="eform-menu-icon" />
                      <Typography variant="inherit">
                        Edit Form Data Tables
                      </Typography>
                    </MenuItem>
                    <MenuItem
                      onClick={() => handleNavigation("/admin/edit-pages")}
                    >
                      <AutoStories className="eform-menu-icon" />
                      <Typography variant="inherit">Edit Pages</Typography>
                    </MenuItem>
                    <MenuItem
                      onClick={() => handleNavigation("/admin/edit-menuitems")}
                    >
                      <MenuOpen className="eform-menu-icon" />
                      <Typography variant="inherit">Edit Menu Items</Typography>
                    </MenuItem>
                    <MenuItem
                      onClick={() =>
                        handleNavigation("/admin/upload-documents")
                      }
                    >
                      <UploadFile className="eform-menu-icon" />
                      <Typography variant="inherit">
                        Upload Documents
                      </Typography>
                    </MenuItem>
                    {adminFormItems.map((item: MenuItemType) => (
                      <MenuItem
                        key={item.PKey}
                        onClick={() => handleNavigation(item.Alias)}
                      >
                        <Typography variant="inherit">{item.Name}</Typography>
                      </MenuItem>
                    ))}
                    <Typography
                      variant="subtitle1"
                      color="primary"
                      className="eform-menu-header"
                    >
                      Sample Result Lookups
                    </Typography>
                    <MenuItem
                      onClick={() => handleNavigation("/admin/list-parameters")}
                    >
                      <ViewList className="eform-menu-icon" />
                      <Typography variant="inherit">
                        Manage Parameters
                      </Typography>
                    </MenuItem>
                    <MenuItem
                      onClick={() =>
                        handleNavigation("/admin/list-result-methods")
                      }
                    >
                      <FactCheck className="eform-menu-icon" />
                      <Typography variant="inherit">
                        Manage Result Methods
                      </Typography>
                    </MenuItem>
                    <MenuItem
                      onClick={() => handleNavigation("/admin/list-units")}
                    >
                      <Scale className="eform-menu-icon" />
                      <Typography variant="inherit">Manage Units</Typography>
                    </MenuItem>
                    <Typography
                      variant="subtitle1"
                      color="primary"
                      className="eform-menu-header"
                    >
                      Users
                    </Typography>
                    <MenuItem
                      onClick={() => window.open(`${loginUrl}/User`, "_blank")}
                    >
                      <HowToReg className="eform-menu-icon" />
                      <Typography variant="inherit">
                        New Registrations
                      </Typography>
                    </MenuItem>
                    {usersItems.map((item: MenuItemType) => (
                      <MenuItem
                        key={item.PKey}
                        onClick={() => handleNavigation(item.Alias)}
                      >
                        <Typography variant="inherit">{item.Name}</Typography>
                      </MenuItem>
                    ))}
                    {isDeveloper && (
                      <>
                        <Typography
                          variant="subtitle1"
                          color="primary"
                          className="eform-menu-header"
                        >
                          Development
                        </Typography>
                        <MenuItem
                          onClick={() =>
                            handleNavigation("/admin/change-status-actions")
                          }
                        >
                          <Typography variant="inherit">
                            Change Status Actions
                          </Typography>
                        </MenuItem>
                        <MenuItem
                          onClick={() => handleNavigation("/admin/edd-mapping")}
                        >
                          <Typography variant="inherit">EDD Mapping</Typography>
                        </MenuItem>
                        <MenuItem
                          onClick={() =>
                            handleNavigation("/admin/edd-translator-download")
                          }
                        >
                          <Typography variant="inherit">
                            EDD Translator Download
                          </Typography>
                        </MenuItem>
                        <MenuItem
                          onClick={() =>
                            handleNavigation("/admin/edd-translator-upload")
                          }
                        >
                          <Typography variant="inherit">
                            EDD Translator Upload
                          </Typography>
                        </MenuItem>
                        {developmentItems.map((item: MenuItemType) => (
                          <MenuItem
                            key={item.PKey}
                            onClick={() => handleNavigation(item.Alias)}
                          >
                            <Typography variant="inherit">
                              {item.Name}
                            </Typography>
                          </MenuItem>
                        ))}
                        <MenuItem
                          onClick={() =>
                            window.open(`${apiUrl}/Admin/logFile`, "_blank")
                          }
                        >
                          <Typography variant="inherit">Server Log</Typography>
                        </MenuItem>
                      </>
                    )}
                  </Menu>
                )}
                <MenuItem onClick={handleMenuClose}>
                  <Key className="eform-menu-icon" />
                  <Typography variant="inherit">Change Password</Typography>
                </MenuItem>
                <MenuItem onClick={handleMenuClose}>
                  <Logout className="eform-menu-icon" />
                  <Typography variant="inherit">Logout</Typography>
                </MenuItem>
              </Menu>
            </>
          )}
        </Toolbar>
      </Container>
      <Box
        sx={{
          width: "100%",
          height: "4px",
          backgroundColor: "orange",
        }}
      />
      {newLayoutOpen && (
        <ModalBox
          action={{
            type: "messagebox",
            payload: {
              title: "Create New Layout",
              layout:
                getPage("designnewformparams")?.Layout ?? designNewFormParams,
              cancelbutton: true,
            },
            successAction: {
              type: "postdata2",
              payload: {
                endpoint: "Form/newlayout",
                dataKey: "FormKey",
              },
              successAction: {
                type: "messagebox",
                payload: {
                  message:
                    "The new layout has been created successfully! Click OK to design the layout or Cancel to return to the previous page.",
                  okbutton: true,
                  cancelbutton: true,
                },
                successAction: {
                  type: "navigateurl",
                  payload: {
                    url: "/edit?formKey=${FormKey}&formMode=design",
                    target: "_self",
                  },
                },
                failureAction: {
                  type: "reloadpage",
                  payload: {},
                },
              },
              failureAction: {
                type: "messagebox",
                payload: {
                  message: "Failed to create layout! (${root.lastApiError})",
                  closebutton: true,
                },
              },
            },
          }}
          data={{}}
          onDataChange={() => {}}
          mode="edit"
          onComplete={() => {
            setNewLayoutOpen(false);
          }}
          onError={() => {
            setNewLayoutOpen(false);
          }}
          onClose={() => {
            setNewLayoutOpen(false);
          }}
          sx={{ minWidth: "300px" }}
        />
      )}
      {newFormOpen && (
        <ModalBox
          action={{
            type: "messagebox",
            payload: {
              title: "Create New Form",
              layout: getPage("newapplication")?.Layout ?? newApplication,
              cancelbutton: true,
            },
            successAction: {
              type: "postdata2",
              payload: {
                endpoint: "Form/new",
                dataKey: "FormKey",
              },
              successAction: {
                type: "loadform",
                payload: {
                  dataKey: "FormKey",
                  formMode: "edit",
                },
              },
              failureAction: {
                type: "messagebox",
                payload: {
                  message: "Failed to create form!",
                  closebutton: true,
                },
              },
            },
          }}
          data={get(formData, "NewApplication")}
          onDataChange={() => {}}
          mode="edit"
          onComplete={() => {
            handleNewFormClose();
          }}
          onError={() => {
            handleNewFormClose();
          }}
          onClose={() => {
            handleNewFormClose();
          }}
          sx={{ minWidth: "300px" }}
        />
      )}
      {uploadEDDOpen && (
        <ModalBox
          action={{
            type: "messagebox",
            payload: {
              title: "Upload New EDD",
              layout: getPage("uploadedd")?.Layout ?? uploadEDD,
              cancelbutton: true,
            },
          }}
          data={get(formData, "UploadEDD")}
          onDataChange={() => {}}
          mode="edit"
          onComplete={() => {
            handleUploadEDDClose();
          }}
          onError={() => {
            handleUploadEDDClose();
          }}
          onClose={() => {
            handleUploadEDDClose();
          }}
          sx={{ minWidth: "300px" }}
        />
      )}
      {deleteFormOpen && (
        <ModalBox
          action={{
            type: "messagebox",
            payload: {
              title: "Delete Form",
              layout: getPage("deleteform")?.Layout ?? deleteForm,
              cancelbutton: true,
            },
            successAction: {
              type: "messagebox",
              payload: {
                message: "Do you want to delete Form ID: ${FormKey}?",
                okbutton: true,
                cancelbutton: true,
              },
              successAction: {
                type: "postdata",
                payload: {
                  endpoint: "Form/adminDelete",
                  dataKey: "FormKey",
                },
                successAction: {
                  type: "messagebox",
                  payload: {
                    message: "Form ID ${FormKey} successfully deleted!",
                    okbutton: true,
                  },
                  successAction: {
                    type: "fetchformdata",
                    payload: {
                      dataKey: "reportResults",
                      postDataKey: "reportFilter",
                    },
                  },
                },
                failureAction: {
                  type: "messagebox",
                  payload: {
                    message: "Failed to delete form!",
                    closebutton: true,
                  },
                },
              },
            },
          }}
          data={{}}
          onDataChange={() => {}}
          mode="edit"
          onComplete={() => {
            setDeleteFormOpen(false);
          }}
          onError={() => {
            setDeleteFormOpen(false);
          }}
          onClose={() => {
            setDeleteFormOpen(false);
          }}
          sx={{ minWidth: "300px" }}
        />
      )}
      {deleteFormTypeOpen && (
        <ModalBox
          action={{
            type: "messagebox",
            payload: {
              title: "Delete Form Type",
              layout: getPage("deleteformtype")?.Layout ?? deleteFormType,
              cancelbutton: true,
            },
            successAction: {
              type: "messagebox",
              payload: {
                message: "Do you want to delete Form Type: ${FormType}?",
                okbutton: true,
                cancelbutton: true,
              },
              successAction: {
                type: "postdata",
                payload: {
                  endpoint: "Form/deletelayout",
                  hideErrorMessage: true,
                },
                successAction: {
                  type: "messagebox",
                  payload: {
                    message: "Form Type: ${FormType} deleted successfully!",
                    okbutton: true,
                  },
                  successAction: {
                    type: "reloadpage",
                    payload: {},
                  },
                },
                failureAction: {
                  type: "messagebox",
                  payload: {
                    message: "${root.lastApiError}",
                    closebutton: true,
                  },
                },
              },
            },
          }}
          data={{}}
          onDataChange={() => {}}
          mode="edit"
          onComplete={() => {
            setDeleteFormTypeOpen(false);
          }}
          onError={() => {
            setDeleteFormTypeOpen(false);
          }}
          onClose={() => {
            setDeleteFormTypeOpen(false);
          }}
          sx={{ minWidth: "300px" }}
        />
      )}
      <Snackbar
        open={!!validationMessage}
        //anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={validationSeverity}
          sx={{ width: "100%" }}
        >
          {validationMessage}
        </Alert>
      </Snackbar>
      <Popover
        open={isPopoverOpen}
        anchorEl={popoverAnchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        slotProps={{
          paper: {
            style: { marginTop: 20, padding: "0px", maxWidth: "300px" }, // Margin to create space and other styles
          },
        }}
      >
        <Paper style={{ padding: "0px 5px", maxWidth: "300px" }}>
          <Typography variant="h6" gutterBottom>
            Validation Details
          </Typography>
          <Tooltip
            title="Clear Errors"
            PopperProps={{
              modifiers: [
                {
                  name: "offset",
                  options: {
                    offset: [0, -10], // move 8px closer to the item
                  },
                },
              ],
            }}
            placement="top"
            arrow
          >
            <IconButton
              size="small"
              onClick={handleClearErrors}
              aria-label="clear errors"
              style={{ position: "absolute", top: 0, right: 0 }}
            >
              <ClearIcon />
            </IconButton>
          </Tooltip>
          <List dense>
            {validationMessages.map(({ key, message, severity }) => (
              <ListItem
                key={key}
                style={{
                  borderBottom: "1px solid orange", // Separator line
                  alignItems: "flex-start",
                }}
              >
                <ListItemIcon sx={{ minWidth: 30, mt: "3px" }}>
                  {severity === "error" ? (
                    <ErrorOutline color="error" fontSize="small" />
                  ) : (
                    <WarningAmberOutlined color="warning" fontSize="small" />
                  )}
                </ListItemIcon>
                <ListItemText primary={message as string} />
              </ListItem>
            ))}
          </List>
        </Paper>
      </Popover>
      <SessionAlertModal
        open={isAlertOpen}
        remainingSeconds={alertRemaining}
        onStay={handleStay} // Resets the timer and closes the modal
        onLogout={handleLogout} // Immediately logs the user off
      />
    </AppBar>
  );
};

export default Header;
